import React, { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { db } from "../firebase";
import { collection, deleteDoc, doc, onSnapshot, setDoc } from "firebase/firestore";
import tokens from "./theme";

const T = tokens;

/* ─────────────────────────────────────────────────────────────────────────
   Environmental Audit Register — redesigned
   • Auto-generated audit numbers (EA-0001, EA-0002 …) per project
   • View button → read-only summary modal
   • Edit button → full edit modal (create + edit share one modal)
   ──────────────────────────────────────────────────────────────────────── */

const COMPLIANCE_OPTIONS = ["Compliant", "Non-Compliant", "Observation"];
const STATUS_OPTIONS = ["Open", "In Progress", "Closed"];

const emptyAuditForm = {
  auditNo: "",
  auditDate: "",
  auditor: "",
  location: "",
  checklistItem: "",
  complianceStatus: "Compliant",
  nonConformance: "",
  correctiveAction: "",
  responsiblePerson: "",
  targetCloseOutDate: "",
  closeOutStatus: "Open",
  evidenceNotes: "",
};

/* ── status → palette ──────────────────────────────────────────────────── */
function statusPalette(status) {
  switch (status) {
    case "Open":        return { bg: T.color.dangerSoft,  fg: T.color.danger,  bd: T.color.dangerBorder };
    case "In Progress": return { bg: T.color.infoSoft,    fg: T.color.info,    bd: T.color.infoBorder };
    case "Closed":      return { bg: T.color.successSoft,  fg: T.color.success, bd: T.color.successBorder };
    default:            return { bg: T.color.surfaceAlt,   fg: T.color.inkMuted, bd: T.color.border };
  }
}

function compliancePalette(status) {
  switch (status) {
    case "Compliant":     return { bg: T.color.successSoft, fg: T.color.success, bd: T.color.successBorder };
    case "Non-Compliant": return { bg: T.color.dangerSoft,  fg: T.color.danger,  bd: T.color.dangerBorder };
    case "Observation":   return { bg: T.color.warningSoft, fg: T.color.warning, bd: T.color.warningBorder };
    default:              return { bg: T.color.surfaceAlt,   fg: T.color.inkMuted, bd: T.color.border };
  }
}

function isOverdue(targetDate, closeOutStatus) {
  if (!targetDate || closeOutStatus === "Closed") return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(targetDate);
  if (Number.isNaN(dueDate.getTime())) return false;
  dueDate.setHours(0, 0, 0, 0);
  return dueDate < today;
}

/* Next audit number from existing EA-#### values; falls back gracefully. */
function nextAuditNo(audits) {
  let max = 0;
  (audits || []).forEach((a) => {
    const m = /^EA-(\d+)$/.exec(String(a.auditNo || "").trim());
    if (m) max = Math.max(max, parseInt(m[1], 10));
  });
  return `EA-${String(max + 1).padStart(4, "0")}`;
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
}

/* ─── small presentational primitives ─────────────────────────────────── */

function Eyebrow({ children, color }) {
  return (
    <div style={{
      fontFamily: T.font.body, fontSize: 10, fontWeight: 700,
      letterSpacing: "0.12em", textTransform: "uppercase",
      color: color || T.color.accent,
    }}>{children}</div>
  );
}

function Pill({ palette, children }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "3px 10px", borderRadius: 999,
      background: palette.bg, color: palette.fg,
      border: `1px solid ${palette.bd}`,
      fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function Btn({ children, onClick, variant = "primary", size = "md", icon, style }) {
  const variants = {
    primary: { bg: T.color.accent, fg: T.color.inkOnAccent, bd: T.color.accent },
    navy:    { bg: T.color.primary, fg: T.color.inkOnDark, bd: T.color.primary },
    subtle:  { bg: T.color.surface, fg: T.color.ink, bd: T.color.borderStrong },
    ghost:   { bg: "transparent", fg: T.color.inkMuted, bd: "transparent" },
    danger:  { bg: T.color.surface, fg: T.color.danger, bd: T.color.dangerBorder },
  };
  const v = variants[variant] || variants.primary;
  const pad = size === "sm" ? "6px 12px" : "9px 16px";
  const fs = size === "sm" ? 12 : 13;
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: pad, fontSize: fs, fontWeight: 600, fontFamily: T.font.body,
        color: v.fg, background: v.bg, border: `1px solid ${v.bd}`,
        borderRadius: T.radius.sm, cursor: "pointer", lineHeight: 1.2,
        transition: T.motion.fast, whiteSpace: "nowrap",
        ...style,
      }}
    >{icon}{children}</button>
  );
}

function Field({ label, helper, children, span }) {
  return (
    <div style={{ gridColumn: span ? `span ${span}` : "auto", minWidth: 0 }}>
      <label style={{
        display: "block", fontSize: 12, fontWeight: 600, color: T.color.ink, marginBottom: 4,
      }}>{label}</label>
      {children}
      {helper && (
        <div style={{ fontSize: 11, color: T.color.inkSubtle, marginTop: 4 }}>{helper}</div>
      )}
    </div>
  );
}

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "9px 11px", fontSize: 13, fontFamily: T.font.body,
  color: T.color.ink, background: T.color.surface,
  border: `1px solid ${T.color.border}`, borderRadius: T.radius.sm,
  outline: "none", transition: T.motion.fast,
};

function StatCard({ label, value, palette, accent }) {
  return (
    <div style={{
      position: "relative", overflow: "hidden",
      background: T.color.surface, border: `1px solid ${T.color.border}`,
      borderRadius: T.radius.md, padding: "16px 18px",
      boxShadow: T.shadow.sm,
    }}>
      <div style={{
        position: "absolute", top: 0, bottom: 0, left: 0, width: 3,
        background: accent || T.color.primary,
      }} />
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
        textTransform: "uppercase", color: T.color.inkSubtle,
      }}>{label}</div>
      <div style={{
        marginTop: 6, fontFamily: T.font.mono, fontSize: 26, fontWeight: 700,
        color: palette || T.color.ink, lineHeight: 1, fontVariantNumeric: "tabular-nums",
      }}>{value}</div>
    </div>
  );
}

/* ─── modal shell (portal) ─────────────────────────────────────────────── */
function Modal({ open, onClose, children, width = 760 }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: T.z.modal, display: "grid", placeItems: "center", padding: 20 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(14, 27, 44, 0.55)", backdropFilter: "blur(4px)" }} />
      <div style={{
        position: "relative", width: `min(${width}px, 96vw)`, maxHeight: "90vh",
        display: "grid", gridTemplateRows: "auto 1fr auto",
        background: T.color.surface, border: `1px solid ${T.color.border}`,
        borderRadius: T.radius.lg, boxShadow: T.shadow.lg, overflow: "hidden",
      }}>
        {children}
      </div>
    </div>,
    document.body
  );
}

function ModalHeader({ eyebrow, title, subtitle, onClose }) {
  return (
    <div style={{
      padding: "18px 22px", borderBottom: `1px solid ${T.color.border}`,
      display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
      background: T.color.surfaceAlt,
    }}>
      <div style={{ minWidth: 0 }}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h3 style={{
          margin: "5px 0 0", fontFamily: T.font.display, fontSize: 19, fontWeight: 700,
          letterSpacing: "-0.02em", color: T.color.ink,
        }}>{title}</h3>
        {subtitle && <p style={{ margin: "4px 0 0", fontSize: 12, color: T.color.inkMuted }}>{subtitle}</p>}
      </div>
      <button onClick={onClose} aria-label="Close" style={{
        flexShrink: 0, width: 32, height: 32, display: "grid", placeItems: "center",
        background: T.color.surface, border: `1px solid ${T.color.border}`,
        borderRadius: T.radius.sm, cursor: "pointer", color: T.color.inkMuted, fontSize: 18, lineHeight: 1,
      }}>×</button>
    </div>
  );
}

/* ─── view (read-only) modal body ──────────────────────────────────────── */
function DetailRow({ label, children, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto", minWidth: 0 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
        textTransform: "uppercase", color: T.color.inkSubtle, marginBottom: 3,
      }}>{label}</div>
      <div style={{ fontSize: 13, color: T.color.ink, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
        {children || <span style={{ color: T.color.inkSubtle }}>—</span>}
      </div>
    </div>
  );
}

function ViewAuditModal({ audit, open, onClose, onEdit }) {
  if (!audit) return null;
  const cp = compliancePalette(audit.complianceStatus);
  const sp = statusPalette(audit.closeOutStatus);
  const overdue = isOverdue(audit.targetCloseOutDate, audit.closeOutStatus);
  return (
    <Modal open={open} onClose={onClose} width={720}>
      <ModalHeader
        eyebrow="Environmental Audit"
        title={audit.auditNo || "Audit"}
        subtitle={`${fmtDate(audit.auditDate)} · ${audit.auditor || "Unassigned auditor"}`}
        onClose={onClose}
      />
      <div style={{ overflow: "auto", padding: "20px 22px" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
          <Pill palette={cp}>{audit.complianceStatus || "—"}</Pill>
          <Pill palette={sp}>{audit.closeOutStatus || "—"}</Pill>
          {overdue && <Pill palette={{ bg: T.color.dangerSoft, fg: T.color.danger, bd: T.color.dangerBorder }}>⚠ Overdue</Pill>}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
          <DetailRow label="Location / Area">{audit.location}</DetailRow>
          <DetailRow label="Checklist Item">{audit.checklistItem}</DetailRow>
          <DetailRow label="Responsible Person">{audit.responsiblePerson}</DetailRow>
          <DetailRow label="Target Close-Out">
            <span style={overdue ? { color: T.color.danger, fontWeight: 700 } : undefined}>
              {fmtDate(audit.targetCloseOutDate)}
            </span>
          </DetailRow>
          <DetailRow label="Non-Conformance" full>{audit.nonConformance}</DetailRow>
          <DetailRow label="Corrective Action" full>{audit.correctiveAction}</DetailRow>
          <DetailRow label="Evidence / Notes" full>{audit.evidenceNotes}</DetailRow>
        </div>
      </div>
      <div style={{
        padding: "14px 22px", borderTop: `1px solid ${T.color.border}`,
        display: "flex", justifyContent: "flex-end", gap: 8, background: T.color.surfaceAlt,
      }}>
        <Btn variant="subtle" onClick={onClose}>Close</Btn>
        <Btn variant="navy" onClick={() => onEdit(audit)}>Edit Audit</Btn>
      </div>
    </Modal>
  );
}

/* ─── edit / create modal body ─────────────────────────────────────────── */
function EditAuditModal({ open, onClose, form, setForm, onSave, isEditing }) {
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  return (
    <Modal open={open} onClose={onClose} width={820}>
      <ModalHeader
        eyebrow={isEditing ? "Edit Audit" : "New Audit"}
        title={isEditing ? `Editing ${form.auditNo}` : `New Audit · ${form.auditNo}`}
        subtitle="Capture the environmental finding, corrective action and close-out tracking."
        onClose={onClose}
      />
      <div style={{ overflow: "auto", padding: "20px 22px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field label="Audit Number" helper="Auto-generated — editable if needed">
            <input style={{ ...inputStyle, fontFamily: T.font.mono }} value={form.auditNo} onChange={set("auditNo")} />
          </Field>
          <Field label="Audit Date" helper="Date the audit was carried out">
            <input type="date" style={inputStyle} value={form.auditDate} onChange={set("auditDate")} />
          </Field>

          <Field label="Auditor">
            <input style={inputStyle} value={form.auditor} onChange={set("auditor")} placeholder="Name of auditor" />
          </Field>
          <Field label="Location / Area">
            <input style={inputStyle} value={form.location} onChange={set("location")} placeholder="Area or work section inspected" />
          </Field>

          <Field label="Checklist Item" span={2}>
            <input style={inputStyle} value={form.checklistItem} onChange={set("checklistItem")} placeholder="Item or environmental aspect inspected" />
          </Field>

          <Field label="Compliance Status">
            <select style={inputStyle} value={form.complianceStatus} onChange={set("complianceStatus")}>
              {COMPLIANCE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Close-Out Status">
            <select style={inputStyle} value={form.closeOutStatus} onChange={set("closeOutStatus")}>
              {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>

          <Field label="Non-Conformance" span={2} helper="Describe the non-conformance or issue identified">
            <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} value={form.nonConformance} onChange={set("nonConformance")} />
          </Field>

          <Field label="Corrective Action" span={2} helper="Required corrective action to close out the finding">
            <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} value={form.correctiveAction} onChange={set("correctiveAction")} />
          </Field>

          <Field label="Responsible Person">
            <input style={inputStyle} value={form.responsiblePerson} onChange={set("responsiblePerson")} placeholder="Person responsible for close-out" />
          </Field>
          <Field label="Target Close-Out Date">
            <input type="date" style={inputStyle} value={form.targetCloseOutDate} onChange={set("targetCloseOutDate")} />
          </Field>

          <Field label="Evidence / Notes" span={2} helper="Supporting notes, evidence, or close-out remarks">
            <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} value={form.evidenceNotes} onChange={set("evidenceNotes")} />
          </Field>
        </div>
      </div>
      <div style={{
        padding: "14px 22px", borderTop: `1px solid ${T.color.border}`,
        display: "flex", justifyContent: "flex-end", gap: 8, background: T.color.surfaceAlt,
      }}>
        <Btn variant="subtle" onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={onSave}>{isEditing ? "Update Audit" : "Save Audit"}</Btn>
      </div>
    </Modal>
  );
}

/* ─── main section ─────────────────────────────────────────────────────── */
export default function EnvironmentalAuditsSection({ currentProjectId }) {
  const [audits, setAudits] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [complianceFilter, setComplianceFilter] = useState("All");

  // modal state
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [auditForm, setAuditForm] = useState(emptyAuditForm);
  const [editingAuditId, setEditingAuditId] = useState(null);
  const [viewingAudit, setViewingAudit] = useState(null);

  useEffect(() => {
    if (!currentProjectId) { setAudits([]); return undefined; }
    const unsubscribe = onSnapshot(
      collection(db, "projects", currentProjectId, "environmentalAudits"),
      (snapshot) => setAudits(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      (error) => console.error("Error loading environmental audits from Firestore", error)
    );
    return () => unsubscribe();
  }, [currentProjectId]);

  const filteredAudits = useMemo(() => {
    return [...audits]
      .filter((row) => {
        const matchesStatus = statusFilter === "All" || row.closeOutStatus === statusFilter;
        const matchesCompliance = complianceFilter === "All" || row.complianceStatus === complianceFilter;
        const haystack = [
          row.auditNo, row.auditor, row.location, row.checklistItem,
          row.nonConformance, row.correctiveAction, row.responsiblePerson, row.evidenceNotes,
        ].join(" ").toLowerCase();
        const matchesSearch = !searchText.trim() || haystack.includes(searchText.toLowerCase());
        return matchesStatus && matchesCompliance && matchesSearch;
      })
      .sort((a, b) => (b.auditNo || "").localeCompare(a.auditNo || "", undefined, { numeric: true }));
  }, [audits, statusFilter, complianceFilter, searchText]);

  const summary = useMemo(() => ({
    totalAudits: audits.length,
    openFindings: audits.filter((i) => i.closeOutStatus === "Open").length,
    overdueFindings: audits.filter((i) => isOverdue(i.targetCloseOutDate, i.closeOutStatus)).length,
    closedFindings: audits.filter((i) => i.closeOutStatus === "Closed").length,
  }), [audits]);

  /* ── actions ── */
  const openCreate = useCallback(() => {
    setAuditForm({ ...emptyAuditForm, auditNo: nextAuditNo(audits), auditDate: new Date().toISOString().slice(0, 10) });
    setEditingAuditId(null);
    setEditOpen(true);
  }, [audits]);

  const openEdit = useCallback((row) => {
    setAuditForm({
      auditNo: row.auditNo || "",
      auditDate: row.auditDate || "",
      auditor: row.auditor || "",
      location: row.location || "",
      checklistItem: row.checklistItem || "",
      complianceStatus: row.complianceStatus || "Compliant",
      nonConformance: row.nonConformance || "",
      correctiveAction: row.correctiveAction || "",
      responsiblePerson: row.responsiblePerson || "",
      targetCloseOutDate: row.targetCloseOutDate || "",
      closeOutStatus: row.closeOutStatus || "Open",
      evidenceNotes: row.evidenceNotes || "",
    });
    setEditingAuditId(row.id);
    setViewOpen(false);
    setEditOpen(true);
  }, []);

  const openView = useCallback((row) => { setViewingAudit(row); setViewOpen(true); }, []);

  const handleSaveAudit = useCallback(async () => {
    if (!currentProjectId) return;
    const auditNo = (auditForm.auditNo || "").trim() || nextAuditNo(audits);
    // keep the doc id stable on edit; for a new audit derive a clean id from the audit number
    const auditId = editingAuditId || auditNo.replace(/[^A-Za-z0-9-]/g, "") || `EA-${String(Date.now()).slice(-6)}`;
    try {
      await setDoc(doc(db, "projects", currentProjectId, "environmentalAudits", auditId), {
        ...auditForm, auditNo, id: auditId,
      });
      setEditOpen(false);
      setAuditForm(emptyAuditForm);
      setEditingAuditId(null);
    } catch (error) {
      console.error("Error saving environmental audit to Firestore", error);
    }
  }, [currentProjectId, auditForm, editingAuditId, audits]);

  const handleDeleteAudit = useCallback(async (id) => {
    if (!currentProjectId) return;
    if (typeof window !== "undefined" && !window.confirm("Delete this environmental audit? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "projects", currentProjectId, "environmentalAudits", id));
    } catch (error) {
      console.error("Error deleting environmental audit from Firestore", error);
    }
  }, [currentProjectId]);

  const th = {
    textAlign: "left", padding: "10px 12px", fontSize: 10, fontWeight: 700,
    letterSpacing: "0.06em", textTransform: "uppercase", color: T.color.inkSubtle,
    borderBottom: `1px solid ${T.color.border}`, whiteSpace: "nowrap", background: T.color.surfaceAlt,
  };
  const td = { padding: "11px 12px", fontSize: 13, color: T.color.ink, borderBottom: `1px solid ${T.color.border}`, verticalAlign: "middle" };

  return (
    <section style={{ fontFamily: T.font.body, color: T.color.ink, maxWidth: 1280, margin: "0 auto" }}>
      {/* header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-end",
        gap: 16, flexWrap: "wrap", marginBottom: 20,
      }}>
        <div>
          <Eyebrow>Compliance · Site & Admin</Eyebrow>
          <h2 style={{
            margin: "6px 0 0", fontFamily: T.font.display, fontSize: 28, fontWeight: 800,
            letterSpacing: "-0.025em", color: T.color.ink,
          }}>Environmental Audit Register</h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: T.color.inkMuted, maxWidth: 620 }}>
            Track environmental audits, findings, corrective actions and close-out status across the project.
          </p>
        </div>
        <Btn variant="primary" onClick={openCreate} icon={<span style={{ fontSize: 16, lineHeight: 1 }}>＋</span>}>
          New Audit
        </Btn>
      </div>

      {/* summary cards */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
        gap: 12, marginBottom: 20,
      }}>
        <StatCard label="Total Audits" value={summary.totalAudits} accent={T.color.primary} />
        <StatCard label="Open Findings" value={summary.openFindings} accent={T.color.danger} palette={summary.openFindings > 0 ? T.color.danger : T.color.ink} />
        <StatCard label="Overdue Findings" value={summary.overdueFindings} accent={T.color.warning} palette={summary.overdueFindings > 0 ? T.color.warning : T.color.ink} />
        <StatCard label="Closed Findings" value={summary.closedFindings} accent={T.color.success} palette={summary.closedFindings > 0 ? T.color.success : T.color.ink} />
      </div>

      {/* filters + table */}
      <div style={{
        background: T.color.surface, border: `1px solid ${T.color.border}`,
        borderRadius: T.radius.lg, boxShadow: T.shadow.sm, overflow: "hidden",
      }}>
        <div style={{
          padding: "16px 18px", borderBottom: `1px solid ${T.color.border}`,
          display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, alignItems: "end",
          background: T.color.surfaceAlt,
        }}>
          <Field label="Search">
            <input style={inputStyle} placeholder="Search audits, findings, people…" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          </Field>
          <Field label="Close-Out Status">
            <select style={inputStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="All">All</option>
              {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Compliance">
            <select style={inputStyle} value={complianceFilter} onChange={(e) => setComplianceFilter(e.target.value)}>
              <option value="All">All</option>
              {COMPLIANCE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>Audit No</th>
                <th style={th}>Date</th>
                <th style={th}>Auditor</th>
                <th style={th}>Location</th>
                <th style={th}>Compliance</th>
                <th style={th}>Responsible</th>
                <th style={th}>Target Close-Out</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAudits.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ ...td, textAlign: "center", color: T.color.inkSubtle, padding: "32px 12px" }}>
                    {audits.length === 0 ? "No environmental audits captured yet — start with “New Audit”." : "No audits match the current filters."}
                  </td>
                </tr>
              ) : (
                filteredAudits.map((row) => {
                  const cp = compliancePalette(row.complianceStatus);
                  const sp = statusPalette(row.closeOutStatus);
                  const overdue = isOverdue(row.targetCloseOutDate, row.closeOutStatus);
                  return (
                    <tr key={row.id} style={{ transition: T.motion.fast }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = T.color.surfaceAlt)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                      <td style={{ ...td, fontFamily: T.font.mono, fontWeight: 700 }}>{row.auditNo || "—"}</td>
                      <td style={td}>{fmtDate(row.auditDate)}</td>
                      <td style={td}>{row.auditor || "—"}</td>
                      <td style={td}>{row.location || "—"}</td>
                      <td style={td}><Pill palette={cp}>{row.complianceStatus || "—"}</Pill></td>
                      <td style={td}>{row.responsiblePerson || "—"}</td>
                      <td style={{ ...td, color: overdue ? T.color.danger : T.color.ink, fontWeight: overdue ? 700 : 500 }}>
                        {fmtDate(row.targetCloseOutDate)}{overdue ? " ⚠" : ""}
                      </td>
                      <td style={td}><Pill palette={sp}>{row.closeOutStatus || "—"}</Pill></td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                          <Btn size="sm" variant="subtle" onClick={() => openView(row)}>View</Btn>
                          <Btn size="sm" variant="navy" onClick={() => openEdit(row)}>Edit</Btn>
                          <Btn size="sm" variant="danger" onClick={() => handleDeleteAudit(row.id)}>Delete</Btn>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* modals */}
      <ViewAuditModal
        audit={viewingAudit}
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        onEdit={openEdit}
      />
      <EditAuditModal
        open={editOpen}
        onClose={() => { setEditOpen(false); setEditingAuditId(null); }}
        form={auditForm}
        setForm={setAuditForm}
        onSave={handleSaveAudit}
        isEditing={!!editingAuditId}
      />
    </section>
  );
}